import type { Socket } from 'socket.io-client'
import { loginResponseSchema } from 'src/shared/apiBackendResponses/login'

export interface FetchProps<Request> {
  api?: string
  route: string
  action: string
  req?: Request
}

export interface SocketResponse {
  response: unknown
  error?: string
}

// Only these reads may be retried after a lost connection. Never replay writes.
const readActions: Record<string, readonly string[]> = {
  produkt: ['get', 'getByIds', 'productTypes', 'crossSellSeries', 'crossSellMatches'],
  stock: ['productStock'], ebayListing: ['list'], ebayAccounts: ['list'],
  brands: ['selectList', 'list'], categories: ['selectList', 'list'], menuPages: ['selectList', 'list'],
  menues: ['selectList', 'list'], filters: ['selectList', 'list', 'forCatergorie'],
  categorie: ['get'], menuPage: ['get'], menu: ['get'],
  produkte: ['list'], produktai: ['list', 'get'], ebayTemplates: ['list'],
  discounts: ['list'], orders: ['list'], crawlers: ['list'], coupons: ['list'], magazines: ['list'],
  brand: ['get'], order: ['get'], coupon: ['get'], filter: ['get'],
  discount: ['get', 'getProductDiscountsForProduct', 'getProductDiscountOverviewForProduct'],
  crawler: ['get'], magazine: ['get', 'getOverview'], ebayTemplate: ['get'],
  productHistorie: ['versionsPreview'], idealo: ['overview'],
}
const lookupRoutes = new Set(['brands', 'categories', 'menuPages', 'menues', 'filters'])
const AUTH_TIMEOUT = 20000
const READ_TIMEOUT = 30000
const LOOKUP_TTL = 30000

class ConnectionError extends Error {}

/** One authenticated session per socket; no request is buffered ahead of login. */
export const createSocketRequests = (socket: Socket) => {
  let ready = false
  let generation = 0
  let authAttempt = 0
  const waiting = new Set<{ resolve: () => void; reject: (error: Error) => void }>()
  const lookups = new Map<string, { expires: number; promise: Promise<unknown> }>()

  const clearAuthentication = () => {
    ready = false
    generation += 1
    authAttempt += 1
    lookups.clear()
    for (const waiter of waiting) waiter.reject(new ConnectionError('Verbindung unterbrochen. Bitte erneut laden.'))
    waiting.clear()
  }
  socket.on('disconnect', clearAuthentication)

  const waitFor = (authenticated: boolean) => new Promise<void>((resolve, reject) => {
    if (socket.connected && (!authenticated || ready)) { resolve(); return }
    const finish = (error?: Error) => {
      clearTimeout(timer)
      waiting.delete(waiter)
      socket.off('connect', onConnect)
      if (error) reject(error)
      else resolve()
    }
    const onConnect = () => { if (!authenticated) finish() }
    const waiter = { resolve: () => finish(), reject: (error: Error) => finish(error) }
    const timer = setTimeout(() => finish(new ConnectionError('Die Verbindung konnte nicht hergestellt werden. Bitte erneut laden.')), AUTH_TIMEOUT)
    waiting.add(waiter)
    if (!authenticated) socket.on('connect', onConnect)
  })

  const rpc = (event: string, request: unknown, timeout: number): Promise<unknown> => new Promise((resolve, reject) => {
    if (!socket.connected) { reject(new ConnectionError('Verbindung unterbrochen. Bitte erneut laden.')); return }
    let settled = false
    const finish = (error?: unknown, response?: unknown) => {
      if (settled) return
      settled = true
      socket.off('disconnect', onDisconnect)
      if (error) reject(error)
      else resolve(response)
    }
    const onDisconnect = () => finish(new ConnectionError('Verbindung unterbrochen. Bitte den aktuellen Stand prüfen.'))
    socket.on('disconnect', onDisconnect)
    try {
      // Socket.IO removes timed-out acknowledgements and unsent buffered packets.
      socket.timeout(timeout).emit(event, request, (error: Error | null, response: unknown) => {
        if (error) finish(new ConnectionError('Die Anfrage hat zu lange gedauert. Bitte erneut laden.'))
        else finish(undefined, response)
      })
    } catch (error) { finish(error) }
  })

  const authenticate = async (request: unknown) => {
    const attempt = ++authAttempt
    ready = false
    lookups.clear()
    await waitFor(false)
    if (attempt !== authAttempt) throw new ConnectionError('Die Anmeldung wurde abgebrochen.')
    let response: unknown
    try { response = await rpc('login', request, AUTH_TIMEOUT) }
    catch (error) {
      if (attempt === authAttempt) {
        for (const waiter of waiting) waiter.reject(new ConnectionError('Die Anmeldung konnte nicht abgeschlossen werden. Bitte erneut laden.'))
        waiting.clear()
      }
      throw error
    }
    if (attempt !== authAttempt) throw new ConnectionError('Die Verbindung wurde zwischenzeitlich erneuert.')
    const parsed = loginResponseSchema.safeParse((response as SocketResponse | null)?.response)
    if (parsed.success && parsed.data !== false && !(response as SocketResponse).error) {
      ready = true
      for (const waiter of waiting) waiter.resolve()
      waiting.clear()
    } else {
      for (const waiter of waiting) waiter.reject(new Error('Die Anmeldung ist abgelaufen.'))
      waiting.clear()
    }
    return response
  }

  const fetch = <Request>({ api = 'fetch', route, action, req }: FetchProps<Request>): Promise<unknown> => {
    const readOnly = api === 'fetch' && Boolean(readActions[route]?.includes(action))
    const lookup = readOnly && action === 'selectList' && lookupRoutes.has(route)
    const key = JSON.stringify([api, route, action, req])
    if (lookup) {
      const previous = lookups.get(key)
      if (previous && previous.expires > Date.now()) return previous.promise
    } else if (!readOnly) {
      // Conservative invalidation also covers writes to related entities.
      generation += 1
      lookups.clear()
    }
    const requestGeneration = generation
    const run = async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          await waitFor(true)
          const result = await rpc(api, { route, action, req }, readOnly ? READ_TIMEOUT : 300000) as SocketResponse
          if (!result || typeof result !== 'object' || !('response' in result)) throw new Error('Ungültige Antwort vom Server.')
          if (result.error) throw new Error(result.error)
          return result.response
        } catch (error) {
          if (!(error instanceof ConnectionError) || !readOnly || attempt > 0) throw error
        }
      }
    }
    const promise = run()
    if (lookup) {
      const entry = { expires: Date.now() + LOOKUP_TTL, promise }
      lookups.set(key, entry)
      promise.then(value => {
        if ((generation !== requestGeneration || !Array.isArray(value)) && lookups.get(key) === entry) lookups.delete(key)
      }, () => { if (lookups.get(key) === entry) lookups.delete(key) })
    }
    return promise
  }
  return { fetch, authenticate, clearAuthentication }
}
